import { MealType } from 'common/MealTypeUtils';
import { urlDateFormat } from 'common/UrlDate';
import React, { useMemo } from 'react';
import { MEAL_TYPE_SELECTOR } from 'src/pages/consumer/dish-first/explore/Constants';
import { getMealTypeId } from 'src/pages/consumer/dish-first/explore/utils';
import { useIsSoldOut } from 'src/pages/consumer/order/sold-out-notifications/useIsSoldOut';
import { WavyDivider } from 'src/shared/design-system/DishFirst/WavyDivider/WavyDivider';
import { useSelector } from 'src/store';
import { selectMenuItemCountById } from 'src/store/cart/selectors';
import styled from 'styled-components';
import { match } from 'ts-pattern';
import { Colors, Spacing } from 'web-common/src/shared/styles';
import {
  ConsumerOrderDishSelectionItem,
  ConsumerOrderDishSelectionItemFormat,
} from '../ConsumerOrderDishSelectionItem';
import { ConsumerOrderDishSelectionRow } from '../ConsumerOrderDishSelectionRow';
import { Section, sectionOrder } from './consts';
import { getRemainingQuantities, useGetCurrentMealType } from './hooks';
import { CollectionAnchor, Container } from './styles';
import { IConsumerOrderDishSelectionProps } from './types';
import { getFoodTypeSection } from './utils';

const Divider = styled(WavyDivider)`
  margin-bottom: ${Spacing.QUADRUPLE};
  margin-top: 8px;
  width: 100%;
`;

interface AnchorSetterProps {
  mealTypes: string[];
}
const AnchorSetter: React.FC<React.PropsWithChildren<AnchorSetterProps>> = ({ mealTypes }) => {
  useGetCurrentMealType(mealTypes);
  return <></>;
};
export const ConsumerOrderDishSelectionSection: React.FC<IConsumerOrderDishSelectionProps> = ({
  foodItems,
  deliveryDate,
  availabilityInfo,
  shefId,
  attachAnchors,
  titleBadge,
  subTitle,
  isUnavailable,
  deliveryDateTime,
  isEditingOrder,
  ...props
}: IConsumerOrderDishSelectionProps) => {
  const sections = useMemo(
    () =>
      sectionOrder
        .map((section) => ({
          title: section.toString(),
          items: foodItems.filter((item) => getFoodTypeSection(item.foodType) === section),
          mealType: match(section)
            .with(Section.Main, () => MealType.Main)
            .with(Section.Side, () => MealType.Side)
            .otherwise(() => section.toString()),
        }))
        .filter(({ items }) => items.length),
    [foodItems]
  );

  const { menuCounts } = useSelector((state) => {
    const deliveryUrlDate = deliveryDate?.toFormat(urlDateFormat);
    return {
      menuCounts: selectMenuItemCountById(state.cart, shefId, deliveryUrlDate),
    };
  });

  const { numDishesRemaining } = useIsSoldOut({
    availabilityInfo,
    deliveryDate,
    shefId,
    isEditingOrder,
  });

  const remainingQuantities = useMemo(
    () => getRemainingQuantities(foodItems, deliveryDateTime, menuCounts, numDishesRemaining),
    [foodItems, deliveryDateTime, menuCounts, numDishesRemaining]
  );

  const mealTypes = sections.map((section) => section.mealType);

  return (
    <Container>
      {sections.map((section) => (
        <>
          {attachAnchors && (
            <AnchorSetter mealTypes={mealTypes}>
              <CollectionAnchor
                data-role={MEAL_TYPE_SELECTOR}
                data-meal-type={section.mealType}
                id={getMealTypeId(section.mealType)}
              />
            </AnchorSetter>
          )}
          <ConsumerOrderDishSelectionRow
            titleBadge={titleBadge}
            subTitle={subTitle}
            title={section.title}
            blur={false}
            key={section.title}>
            {section.items.map((foodItem) => (
              <ConsumerOrderDishSelectionItem
                {...props}
                format={ConsumerOrderDishSelectionItemFormat.ORDER}
                key={foodItem.id}
                foodItem={foodItem}
                isUnavailable={isUnavailable}
                remainingQuantity={remainingQuantities[foodItem.id]}
                quantity={menuCounts[foodItem.id] ?? 0}
                isEditingOrder={isEditingOrder}
              />
            ))}
          </ConsumerOrderDishSelectionRow>
          <Divider backgroundColor={Colors.GRAY_LITE_30} borderColor={Colors.GRAY} amplitude={12} frequency={12} />
        </>
      ))}
    </Container>
  );
};
